/**
 * Frozen image-analytics event names and their PII-free property allowlists.
 *
 * These names are the cross-platform contract: mobile mirrors them verbatim
 * and both repos snapshot-test against their own copy, so a rename anywhere
 * fails a test instead of silently forking the metric. Properties are
 * allowlisted — a `surface` is always an enum, never a storage path, and no
 * filename, email, or uid may ever ride along.
 */

export const IMAGE_EVENTS = Object.freeze({
  uploadSucceeded: "image_upload_succeeded",
  uploadFailed: "image_upload_failed",
  resized: "image_resized",
  variantServed: "variant_served",
  fallbackServed: "fallback_served",
  variantCoverage: "variant_coverage",
} as const);

export type ImageEventName = (typeof IMAGE_EVENTS)[keyof typeof IMAGE_EVENTS];

export const UPLOAD_FAILURE_REASONS = Object.freeze([
  "size",
  "permission",
  "network",
  "resize_error",
] as const);

export type UploadFailureReason = (typeof UPLOAD_FAILURE_REASONS)[number];

/**
 * Every legal `surface` label, upload-side and serving-side. Typed as a
 * union so a storage path, uid, or free-form string is a COMPILE error —
 * the structural guarantee behind "a surface is always an enum". Serving
 * consumers add their labels here as they wire up (avatars first).
 */
export const IMAGE_SURFACES = Object.freeze([
  "profile",
  "coverArt",
  "boxLogo",
  "boxBanner",
  "boxCoverArt",
  "landingBanner",
  "avatar",
] as const);

export type ImageSurface = (typeof IMAGE_SURFACES)[number];

/** The only properties each event may carry. Anything else is dropped. */
export const IMAGE_EVENT_ALLOWED_PROPS: Readonly<Record<ImageEventName, readonly string[]>> =
  Object.freeze({
    [IMAGE_EVENTS.uploadSucceeded]: ["surface"],
    [IMAGE_EVENTS.uploadFailed]: ["surface", "reason"],
    [IMAGE_EVENTS.resized]: ["surface", "bytes_before", "bytes_after", "duration_ms"],
    [IMAGE_EVENTS.variantServed]: ["surface", "size"],
    [IMAGE_EVENTS.fallbackServed]: ["surface", "size"],
    [IMAGE_EVENTS.variantCoverage]: ["surface", "hits", "fallbacks"],
  });
